// Camada de query das cobranças do seller (`spec/17-frontend.md` §4.5 e §4.7).
//
// Endpoints do PAINEL: `/v1/me/transactions*`. `/v1/transactions*` existe e é a
// superfície de **API key** (`spec/17` §8.3), não é isto que a tela consome.
//
// Nenhum tipo de resposta escrito à mão: tudo derivado de `api/types/public.d.ts`
// (`spec/17` §5.2).

import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query';
import type { Mode } from '../../app/mode.tsx';
import { api } from '../client.ts';
import type { paths } from '../types/public.d.ts';

type ListOk = paths['/v1/me/transactions']['get']['responses'][200]['content']['application/json'];
type DetailOk =
  paths['/v1/me/transactions/{id}']['get']['responses'][200]['content']['application/json'];
type TimelineOk =
  paths['/v1/me/transactions/{id}/timeline']['get']['responses'][200]['content']['application/json'];

type PaymentLinkOk =
  paths['/v1/me/payment-links/{id}']['get']['responses'][200]['content']['application/json'];

type SplitAuthorizationsOk =
  paths['/v1/me/split-authorizations']['get']['responses'][200]['content']['application/json'];
type SplitQuotaOk =
  paths['/v1/me/splits/quota']['get']['responses'][200]['content']['application/json'];

type EventsOk = paths['/v1/me/events']['get']['responses'][200]['content']['application/json'];
type DeliveriesOk =
  paths['/v1/me/webhook-deliveries']['get']['responses'][200]['content']['application/json'];

export type TransactionListPage = ListOk;
export type Transaction = ListOk['data'][number];
export type TransactionDetail = DetailOk;
export type TimelineItem = TimelineOk['data'][number];
/** Agregado de `with_totals=1` (API-21): tabs (`by_status`) e resumo (`filtered`). */
export type TransactionTotals = NonNullable<ListOk['totals']>;
export type WebhookDelivery = DeliveriesOk['data'][number];
/** Um split da cobrança na visão do pagador (`spec/19` §5.1). */
export type TransactionSplit = NonNullable<TransactionDetail['splits']>[number];
export type SplitQuota = SplitQuotaOk;
/** Recebedor da lista do próprio seller (`GET /v1/me/split-authorizations`, `spec/19` §5.3). */
export interface AuthorizedRecipient {
  email: string;
}

/** Filtros que viajam na query string da rota (`spec/17` §3). */
export interface TransactionFilters {
  /** Um dos 6 status de `spec/05` §4.3; `undefined` = todos. */
  status?: string | undefined;
  /** `YYYY-MM-DD` em `America/Sao_Paulo`. */
  from?: string | undefined;
  to?: string | undefined;
  /**
   * Qual data o período filtra (croqui: o segundo `Select` da barra). `created` bate
   * em `created_from/to`; `paid` em `paid_from/to`, o mesmo par que a API já aceita.
   */
  field?: 'created' | 'paid' | undefined;
  /** Id público (`txn_…`), email exato ou documento só com dígitos. */
  q?: string | undefined;
  /** Só as cobranças de um link de pagamento (`plk_…`, `spec/29` §5.4). */
  paymentLinkId?: string | undefined;
  /** Só as cobranças de um método (`payment_method` da API, CRYPTO-11); `undefined` = todos. */
  paymentMethod?: PaymentMethodFilter | undefined;
}

export const PAYMENT_METHOD_FILTERS = ['pix', 'voucher', 'crypto', 'card', 'wallet'] as const;
export type PaymentMethodFilter = (typeof PAYMENT_METHOD_FILTERS)[number];

/** `limit` default de `spec/17` §4.7. */
export const PAGE_LIMIT = 50;

/**
 * CONTRATO DE QUERY KEY (`app/live-events.ts`): **domínio primeiro, `mode` depois**.
 * `invalidateQueries` casa por prefixo, então `['transactions', …]` é o que o mapa de
 * `spec/17` §4.5 invalida.
 */
export const transactionsKeys = {
  all: ['transactions'] as const,
  list: (mode: Mode, filters: TransactionFilters) =>
    ['transactions', mode, 'list', filters] as const,
  detail: (mode: Mode, id: string) => ['transactions', mode, id] as const,
  timeline: (mode: Mode, id: string) => ['transactions', mode, id, 'timeline'] as const,
  deliveries: (mode: Mode, id: string) => ['transactions', mode, id, 'deliveries'] as const,
  paymentLink: (mode: Mode, id: string) => ['transactions', mode, 'payment-link', id] as const,
};

/**
 * Split na Nova cobrança (`spec/19` §4.1 e §4.4). Autorização não tem `mode` (é relação
 * entre contas), a cota tem. Mesmo contrato de key: domínio primeiro.
 */
export const splitKeys = {
  granted: ['split-authorizations', 'list'] as const,
  quota: (mode: Mode) => ['splits', mode, 'quota'] as const,
};

export function authorizedRecipientsOptions() {
  return queryOptions({
    queryKey: splitKeys.granted,
    queryFn: async ({ signal }): Promise<AuthorizedRecipient[]> => {
      const page = await api<SplitAuthorizationsOk>('/v1/me/split-authorizations', { signal });
      return page.data.map((item) => ({ email: item.recipient_email }));
    },
  });
}

export function splitQuotaOptions(mode: Mode) {
  return queryOptions({
    queryKey: splitKeys.quota(mode),
    queryFn: ({ signal }) => api<SplitQuotaOk>('/v1/me/splits/quota', { mode, signal }),
  });
}

/**
 * A API valida `created_from`/`created_to` com `z.iso.datetime()`, que em Zod v4 recusa
 * offset: o valor tem de ser um instante em `Z`. Como o filtro é um DIA no fuso de São
 * Paulo, o offset do dia pedido é medido pelo próprio `Intl` (nada de `−03:00` chumbado,
 * mesmo o Brasil tendo abolido o horário de verão em 2019).
 */
const OFFSET_FORMAT = new Intl.DateTimeFormat('en-US', {
  timeZone: 'America/Sao_Paulo',
  timeZoneName: 'longOffset',
});

export function startOfDayInSaoPaulo(isoDate: string): string {
  const parts = OFFSET_FORMAT.formatToParts(new Date(`${isoDate}T12:00:00Z`));
  const name = parts.find((part) => part.type === 'timeZoneName')?.value ?? 'GMT-03:00';
  const offset = name.replace('GMT', '');
  return new Date(`${isoDate}T00:00:00${offset === '' ? 'Z' : offset}`).toISOString();
}

/**
 * `created_to` entra na API como `<=`, então o fim do período é o **começo do dia
 * seguinte**: mandar o começo do dia `to` descartaria o último dia inteiro.
 */
export function queryForFilters(filters: TransactionFilters): Record<string, string | number> {
  const query: Record<string, string | number> = { limit: PAGE_LIMIT };
  if (filters.status !== undefined && filters.status !== '') {
    query.status = filters.status;
  }
  const prefix = filters.field === 'paid' ? 'paid' : 'created';
  if (filters.from !== undefined && filters.from !== '') {
    query[`${prefix}_from`] = startOfDayInSaoPaulo(filters.from);
  }
  if (filters.to !== undefined && filters.to !== '') {
    query[`${prefix}_to`] = startOfDayInSaoPaulo(nextDay(filters.to));
  }
  if (filters.q !== undefined && filters.q !== '') {
    query.q = filters.q;
  }
  if (filters.paymentLinkId !== undefined && filters.paymentLinkId !== '') {
    query.payment_link_id = filters.paymentLinkId;
  }
  if (filters.paymentMethod !== undefined) {
    query.payment_method = filters.paymentMethod;
  }
  return query;
}

function nextDay(isoDate: string): string {
  const shifted = new Date(`${isoDate}T12:00:00Z`);
  shifted.setUTCDate(shifted.getUTCDate() + 1);
  return shifted.toISOString().slice(0, 10);
}

/**
 * Paginação por cursor, nunca por página numerada (`spec/00` §4.6): `next_cursor` nulo
 * é o fim.
 *
 * `refetchInterval` de 30 s é o fallback obrigatório de `spec/17` §4.5 para a lista
 * aberta, hoje o SSE nem é alcançável pelo navegador (`FIX-CORS-SSE`), e chamar
 * `useLiveEvents()` aqui abriria um segundo `EventSource` em cima do do shell.
 */
export function transactionsListOptions(mode: Mode, filters: TransactionFilters) {
  return infiniteQueryOptions({
    queryKey: transactionsKeys.list(mode, filters),
    queryFn: ({ pageParam, signal }) =>
      api<TransactionListPage>('/v1/me/transactions', {
        // `with_totals=1` (API-21) alimenta as abas (`by_status`, que ignora o filtro de
        // status) e o resumo à direita (`filtered`). Uma query agregada por página.
        query: { ...queryForFilters(filters), with_totals: '1', cursor: pageParam },
        mode,
        signal,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (page: TransactionListPage) => page.next_cursor ?? undefined,
    refetchInterval: 30_000,
  });
}

export function transactionOptions(mode: Mode, id: string) {
  return queryOptions({
    queryKey: transactionsKeys.detail(mode, id),
    queryFn: ({ signal }) => api<TransactionDetail>(`/v1/me/transactions/${id}`, { mode, signal }),
  });
}

/** Título do link para o chip do filtro; a tela cai no slug se a consulta falhar. */
export function paymentLinkOptions(mode: Mode, id: string) {
  return queryOptions({
    queryKey: transactionsKeys.paymentLink(mode, id),
    queryFn: ({ signal }) => api<PaymentLinkOk>(`/v1/me/payment-links/${id}`, { mode, signal }),
    retry: false,
  });
}

export function transactionTimelineOptions(mode: Mode, id: string, enabled: boolean) {
  return queryOptions({
    queryKey: transactionsKeys.timeline(mode, id),
    queryFn: ({ signal }) =>
      api<TimelineOk>(`/v1/me/transactions/${id}/timeline`, { mode, signal }),
    enabled,
  });
}

/**
 * Estorno pedido pelo painel (API-21, `POST /v1/me/transactions/{id}/refund`).
 *
 * `Idempotency-Key` é OBRIGATÓRIA e o corpo é `{}`, com `Content-Type: application/json`
 * e corpo vazio o parse falha antes da rota (API-21 "Questões abertas" item 7).
 *
 * O step-up de MFA é incondicional nesta rota e vem de graça: `api()` reexecuta a mesma
 * chamada, com a MESMA key, depois que o `StepUpDialog` valida o código.
 */
export function refundTransaction(
  mode: Mode,
  id: string,
  idempotencyKey: string,
): Promise<TransactionDetail> {
  return api<TransactionDetail>(`/v1/me/transactions/${id}/refund`, {
    method: 'POST',
    body: {},
    idempotencyKey,
    mode,
  });
}

/** Teto de eventos consultados por cobrança: uma venda gera poucos (`spec/19` §3). */
const DELIVERY_EVENTS_LIMIT = 20;

/**
 * "Entregas" do croqui: as entregas de webhook DESTA cobrança. São dois saltos porque a
 * API não filtra entrega por recurso (API-25): `events?resource_id` devolve os eventos da
 * transação e `webhook-deliveries?event_id` as entregas de cada um.
 */
export function transactionDeliveriesOptions(mode: Mode, id: string, enabled: boolean) {
  return queryOptions({
    queryKey: transactionsKeys.deliveries(mode, id),
    enabled,
    queryFn: async ({ signal }) => {
      const events = await api<EventsOk>('/v1/me/events', {
        query: { resource_type: 'transaction', resource_id: id, limit: DELIVERY_EVENTS_LIMIT },
        mode,
        signal,
      });
      const pages = await Promise.all(
        events.data.map((event) =>
          api<DeliveriesOk>('/v1/me/webhook-deliveries', {
            query: { event_id: event.id },
            mode,
            signal,
          }),
        ),
      );
      return pages.flatMap((page) => page.data);
    },
  });
}
